import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import SettingsPanel from './SettingsPanel';
import RangeSliderFilter from './RangeSliderFilter';
import {
    setLoudnessErrorMessage
} from '../redux/slices/loudnessSlice';
import {
    setLoudnessLevel,
    setInputLoudnessLevel,
    setChangeLoudness,
    setAddLoudnessToFileNames,
    setBackupLoudnessOriginalFiles
} from '../redux/slices/loudnessSettingsSlice';
import { useVisibleLoudnessRows, useReachableLoudness } from '../hooks/useReachableLoudness';
import { getEnrichedParts } from '../hooks/useEnrichedTracks';
import { track } from '../utils/analytics';

const LoudnessSettings = () => {
    const dispatch = useDispatch();
    const {loudnessErrorMessage} = useSelector((state) => state.loudness);
    const {
        inputLoudnessLevel,
        changeLoudness,
        loudnessLevel,
    } = useSelector((state) => state.loudnessSettings);

    // Persistence: loudnessSettings is registered in PERSISTED_SLICES, so the
    // settings middleware saves every change as it happens. A save-on-close
    // handler used to live here — it lost changes on crash/force-quit.

    // The loudest level every track in view can reach without clipping —
    // the same scope and math as the Match run (shared hook). Used only to
    // catch a target ABOVE it; see reachableHint below for why it is never
    // advertised on its own.
    const visibleRows = useVisibleLoudnessRows();
    const reachable = useReachableLoudness(visibleRows, loudnessLevel);
    const limiterPath = reachable?.limitedBy?.filePath;
    // Tooltip only — the filename-parsed label is plenty here.
    const limiterName = limiterPath ? getEnrichedParts(null, limiterPath).label : null;
    const overCeiling = !!(changeLoudness && reachable && Number(loudnessLevel) > reachable.lufs);
    const shortCount = overCeiling ? reachable.shortRows.length : 0;

    const applySuggested = () => {
        dispatch(setLoudnessLevel(reachable.lufs));
        dispatch(setInputLoudnessLevel(String(reachable.lufs)));
        dispatch(setLoudnessErrorMessage(""));
        track('loudness_target_suggestion_applied', { from: 'hint', level: reachable.lufs, track_count: reachable.trackCount });
    };

    const handleLoudnessLevelChange = (value) => {
        dispatch(setInputLoudnessLevel(value));

        if (value === "" || isNaN(value) || value > 0 || value < -60) {
            dispatch(setLoudnessErrorMessage("Between 0 and -60"));
        } else {
            dispatch(setLoudnessErrorMessage(""));
        }
    };

    const handleLoudnessLevelBlur = () => {
        if (inputLoudnessLevel === "" || isNaN(inputLoudnessLevel) || inputLoudnessLevel > 0 || inputLoudnessLevel < -100) {
            dispatch(setInputLoudnessLevel(loudnessLevel));
        } else {
            // The input field yields strings — store the level as a number so
            // strict comparisons (matched counter, warning) behave.
            dispatch(setLoudnessLevel(Number(inputLoudnessLevel)));
        }
        dispatch(setLoudnessErrorMessage(""));
    };

    const filterSection = (
        <RangeSliderFilter
            step={0.1}
            label="Loudness Filter (LUFS)"
        />
    );

    const loudnessLevelInput = (
        <div className="loudness-label-tooltip-container">
            <label className={`loudness-form-label ${!changeLoudness ? 'disabled' : ''}`}>
                Choose Loudness Level
            </label>
            <input
                className="loudness-input"
                type="number"
                value={inputLoudnessLevel}
                min="-60"
                max="0"
                step="0.1"
                onChange={(e) => handleLoudnessLevelChange(e.target.value)}
                onBlur={handleLoudnessLevelBlur}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        handleLoudnessLevelBlur();
                    }
                }}
                disabled={!changeLoudness}
            />
            {loudnessErrorMessage && (
                <div className="text-danger">{loudnessErrorMessage}</div>
            )}
        </div>
    );

    // The ceiling is a LIMIT, not a recommendation: louder isn't the goal
    // (consistent is), so a "loudest you could go" line with a Use button
    // would nudge people upward for no reason. Silent while the target is
    // reachable; only when it's above the ceiling do we state the consequence
    // and offer the level that actually works. Never fires for filename-only
    // runs.
    const reachableHint = (
        overCeiling ? (
            <div className="loudness-reachable-hint over">
                <div className="loudness-reachable-line">
                    {shortCount === 1 ? '1 track' : `${shortCount} tracks`} can’t reach {loudnessLevel} LUFS without clipping — {shortCount === 1 ? 'it’ll' : 'they’ll'} end up quieter than the rest.
                </div>
                <div className="loudness-reachable-line">
                    Loudest all can share:{' '}
                    <span className="loudness-reachable-value">
                        <strong title={limiterName ? `Limited by ${limiterName}` : undefined}>{reachable.lufs} LUFS</strong>
                        <button type="button" className="loudness-reachable-use" onClick={applySuggested}>Use</button>
                    </span>
                </div>
            </div>
        ) : <div/>
    );

    return (
        <>
            {/*<div className="loudness-instructions-text">*/}
            {/*    <span>*/}
            {/*        Toggle "Add Loudness to Filenames" and "Match Loudness to Chosen Value" to edit filenames only, match loudness, or both.*/}
            {/*        <br />You can also edit filenames without changing loudness.*/}
            {/*    </span>*/}
            {/*</div>*/}

            <SettingsPanel
                filtersTitle="Loudness Filter"
                filtersComponent={filterSection}
                showFilterSeparator={true}
                settings={[
                    {
                        id: 'changeLoudness',
                        selector: 'changeLoudness',
                        label: 'Match Loudness to Chosen Value',
                        action: setChangeLoudness
                    },
                    {
                        id: 'addLoudnessToFileNames',
                        selector: 'addLoudnessToFileNames',
                        label: 'Add Loudness to Filenames',
                        action: setAddLoudnessToFileNames
                    },
                    {
                        id: 'backupLoudnessOriginalFiles',
                        selector: 'backupLoudnessOriginalFiles',
                        label: 'Keep Original Files Untouched',
                        tooltip: 'On: saves a loudness-matched copy next to each original. Off: re-encodes the file in place.',
                        action: setBackupLoudnessOriginalFiles
                    },
                    {
                        id: 'customLoudnessInput',
                        customComponent: loudnessLevelInput
                    },
                    {
                        id: 'reachableHint',
                        customComponent: reachableHint
                    }
                ]}
                sliceSelector={(state) => state.loudnessSettings}
            />
        </>
    );
};

export default LoudnessSettings;
